import { useState, useRef, useCallback, useEffect } from 'react';

interface VinScannerProps {
  onVinScanned: (vin: string) => void;
}

/**
 * Unified VIN scanner — camera feed + manual input on one screen.
 * Uses BarcodeDetector API (Chrome/Edge/Samsung) with live video feed.
 * Camera section collapses gracefully if unavailable.
 */
export default function VinScanner({ onVinScanned }: VinScannerProps) {
  const [manualVin, setManualVin] = useState('');
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraAvailable, setCameraAvailable] = useState(true);
  const [error, setError] = useState('');
  const [scanStatus, setScanStatus] = useState('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanIntervalRef = useRef<number | null>(null);
  const detectedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const stopCamera = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraReady(false);
  }, []);

  // Clean up camera on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  const isValidVin = (str: string): boolean => {
    const clean = str.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
    return clean.length === 17 && !/[IOQ]/.test(clean);
  };

  const handleDetection = useCallback(
    (vin: string) => {
      if (detectedRef.current) return;
      const clean = vin.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
      if (isValidVin(clean)) {
        detectedRef.current = true;
        stopCamera();

        // Haptic feedback on mobile
        if ('vibrate' in navigator) navigator.vibrate(100);

        onVinScanned(clean);
      }
    },
    [onVinScanned, stopCamera]
  );

  // Start live camera with barcode scanning
  const startCamera = useCallback(async () => {
    setError('');
    setScanStatus('Starting camera...');
    detectedRef.current = false;

    const hasBarcodeAPI = 'BarcodeDetector' in window;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          // @ts-expect-error -- focusMode not in all TS defs
          focusMode: 'continuous',
        },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraReady(true);
        setScanStatus('Point at VIN barcode...');
      }

      if (hasBarcodeAPI) {
        // @ts-expect-error -- BarcodeDetector not in all TS defs
        const detector = new BarcodeDetector({
          formats: ['code_39', 'code_128', 'data_matrix', 'qr_code'],
        });

        scanIntervalRef.current = window.setInterval(async () => {
          if (!videoRef.current || videoRef.current.readyState < 2 || detectedRef.current) return;

          try {
            const barcodes = await detector.detect(videoRef.current);
            for (const barcode of barcodes) {
              const raw = barcode.rawValue?.trim();
              if (raw && raw.length >= 17) {
                handleDetection(raw);
                return;
              }
            }
          } catch {
            // Frame decode error — ignore, retry next interval
          }
        }, 250);
      } else {
        setScanStatus('Auto-scan not supported — type VIN below');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Camera failed';
      if (msg.includes('Permission') || msg.includes('denied')) {
        setError('Camera access denied. Use manual entry below.');
      } else if (msg.includes('NotFound') || msg.includes('Requested device not found')) {
        setError('No camera found.');
      } else {
        setError(`Camera error: ${msg}`);
      }
      setCameraAvailable(false);
    }
  }, [handleDetection]);

  // Auto-start camera on mount
  useEffect(() => {
    startCamera();
  }, [startCamera]);

  const handleManualInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '').slice(0, 17);
    setManualVin(val);
    setError('');
    // Auto-submit on 17 valid chars
    if (val.length === 17 && isValidVin(val)) {
      onVinScanned(val);
    }
  };

  return (
    <div className="space-y-4">
      {/* Camera section — collapses if unavailable */}
      {cameraAvailable && (
        <div className="relative rounded-2xl overflow-hidden bg-black" style={{ aspectRatio: '16/9' }}>
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            playsInline
            muted
            autoPlay
          />

          {/* Scanning overlay */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <div className="w-[85%] h-12 border-2 border-brand-400 rounded-lg relative">
              <div className="absolute inset-x-0 h-0.5 bg-brand-400 animate-pulse top-1/2" />
              <div className="absolute -top-0.5 -left-0.5 w-3 h-3 border-t-2 border-l-2 border-brand-400 rounded-tl" />
              <div className="absolute -top-0.5 -right-0.5 w-3 h-3 border-t-2 border-r-2 border-brand-400 rounded-tr" />
              <div className="absolute -bottom-0.5 -left-0.5 w-3 h-3 border-b-2 border-l-2 border-brand-400 rounded-bl" />
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 border-b-2 border-r-2 border-brand-400 rounded-br" />
            </div>
            <p className="text-white/80 text-xs mt-2 bg-black/40 px-3 py-1 rounded-full">
              {scanStatus}
            </p>
          </div>

          <canvas ref={canvasRef} className="hidden" />

          {/* Loading spinner */}
          {!cameraReady && !error && (
            <div className="absolute inset-0 bg-surface-900 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="card p-3 border-red-800 bg-red-900/20">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Divider */}
      {cameraAvailable && (
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-surface-700" />
          <span className="text-xs text-surface-500 uppercase tracking-wider">or type it</span>
          <div className="flex-1 h-px bg-surface-700" />
        </div>
      )}

      {/* Manual VIN input — always visible */}
      <div>
        <input
          ref={inputRef}
          type="text"
          placeholder="Enter 17-digit VIN"
          className="input w-full text-center text-xl tracking-[0.25em] font-mono uppercase"
          value={manualVin}
          onChange={handleManualInput}
          maxLength={17}
          autoCapitalize="characters"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="done"
        />
        <div className="flex items-center gap-3 mt-2">
          <span className="text-xs text-surface-500 tabular-nums">{manualVin.length}/17</span>
          <div className="flex-1 h-1.5 bg-surface-700 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-200 ${
                manualVin.length === 17 ? 'bg-green-500' : 'bg-brand-500'
              }`}
              style={{ width: `${(manualVin.length / 17) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {manualVin.length === 17 && (
        <button onClick={() => onVinScanned(manualVin)} className="btn-primary w-full text-lg py-4">
          Continue
        </button>
      )}
    </div>
  );
}
